///
/// EarthBrowser
///
/// Created by Matt Giger on 4/14/2026
/// Copyright (c) 2026 Geodata Labs
///

import { AnchoredPanelShell, type PanelShellContext } from "@/components/controls/anchored-panel/AnchoredPanelShell"
import { Badge, CoordFooter, formatTimeAgo, InfoField, MoreSection, SectionTitle } from "@/components/controls/anchored-panel/panelAtoms"
import { type BuoyCategory, buoySourceLabel, cBuoyCategoryLabel, cDartModeLabel, classifyBuoy, compositeObservation, isDartEventMode, isDartReporting, waveDescription } from "@/earthbrowser/dataset-distribution/buoys/buoyClassification"
import { convertTemperature, formatSpeed, formatWaveHeight, getTemperatureUnit } from "@/earthbrowser/utils/unitFormatters"
import { useBuoyObservations } from "@/hooks/useBuoyObservations"
import type { BuoyEntry, BuoyObservation } from "@shared-types/buoy"
import { useEffect, useMemo, useState } from "react"

// NDBC BuoyCAM snapshot: stable URL that updates in place roughly every
// 30-60 minutes during local daylight; refresh politely
const BUOYCAM_IMAGE_URL = (stationId: string) => `https://www.ndbc.noaa.gov/buoycam.php?station=${stationId}`
const BUOYCAM_REFRESH_MS = 15 * 60_000

function formatCardinal(deg: number): string {
	const dirs = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"]
	return dirs[Math.round(deg / 22.5) % 16]
}

function panelCategory(buoy: BuoyEntry): BuoyCategory {
	return classifyBuoy(buoy, buoy.latest_observation)
}

/** Source attribution plus the operating owner when it isn't the source itself */
function attributionLine(buoy: BuoyEntry): string {
	const source = buoySourceLabel(buoy.source)
	const owner = buoy.owner?.trim()
	if (!owner || source.toUpperCase().includes(owner.toUpperCase())) return source
	return `${source} · ${owner}`
}

type TrendWord = "Building" | "Easing" | "Steady"

/**
 * Wave trend over the most recent 24 h: compare the mean of the earliest
 * third against the latest third of observed wave heights
 */
function computeWaveTrend(history: BuoyObservation[]): TrendWord | null {
	const cutoff = Date.now() - 24 * 3600_000
	const values = history
		.filter(o => o.wave_height != null && new Date(o.time).getTime() >= cutoff)
		.map(o => o.wave_height!)
	if (values.length < 4) return null

	const third = Math.max(1, Math.floor(values.length / 3))
	const mean = (arr: number[]) => arr.reduce((sum, v) => sum + v, 0) / arr.length
	const delta = mean(values.slice(-third)) - mean(values.slice(0, third))

	if (delta > 0.3) return "Building"
	if (delta < -0.3) return "Easing"
	return "Steady"
}

function TrendBadge({ trend }: { trend: TrendWord }) {
	const color = trend === "Building" ? "text-amber-400"
		: trend === "Easing" ? "text-emerald-400"
		: "text-slate-400"
	const arrow = trend === "Building" ? "↗" : trend === "Easing" ? "↘" : "→"
	return <span className={color}>{arrow} {trend}</span>
}

export function AnchoredBuoyInfoPanel() {
	return (
		<AnchoredPanelShell
			match={data => data?.type === "buoy" ? data : null}
			fallbackTitle={content => content.buoy.name || content.buoy.station_id}
			contentKey={content => content.buoy.station_id}
			accentBorder={content => {
				const category = panelCategory(content.buoy)
				return category === "inactive" ? "border-slate-500/20" : category === "dart" ? "border-violet-400/20" : "border-teal-400/20"
			}}
			pill={content => {
				const category = panelCategory(content.buoy)
				const accentBar = category === "inactive" ? "bg-slate-500/60" : category === "dart" ? "bg-violet-400/90" : "bg-teal-400/90"
				return <div className={`h-[3px] w-11 ${accentBar} rounded-full`} />
			}}
			cardClassName={content => panelCategory(content.buoy) === "inactive" ? "opacity-80" : ""}
			// No minHeight: the engine reserves the tallest layout for
			// placement, but short categories should size to their content
			lockMinHeight={false}
		>
			{(content, ctx) => <BuoyBody buoy={content.buoy} ctx={ctx} />}
		</AnchoredPanelShell>
	)
}

function BuoyBody({ buoy, ctx }: { buoy: BuoyEntry; ctx: PanelShellContext }) {
	const category = panelCategory(buoy)
	const isDefunct = category === "inactive"
	const isDart = category === "dart"

	// 3 days of observation history for the combined conditions/trends card
	// and the DART water-column trace (skipped for silent stations; the hook
	// skips when stationId is empty)
	const { data: historyData } = useBuoyObservations({
		stationId: isDefunct ? '' : buoy.station_id,
		hours: 72
	})
	const history = useMemo(() => {
		const observations = historyData?.observations ?? []
		return [...observations].sort((a, b) => a.time.localeCompare(b.time))
	}, [historyData])
	const waveTrend = useMemo(() => computeWaveTrend(history), [history])

	// NDBC rows are partial (half-hourly met without waves, hourly waves
	// without met), so fill the newest row's gaps from the last few hours
	// rather than showing "—" for a reading the station made 30 minutes ago
	const obs = useMemo(() => {
		const latest = buoy.latest_observation
		if (!latest || history.length === 0) return latest
		return compositeObservation(latest, history)
	}, [buoy.latest_observation, history])

	const accentText = isDefunct ? "text-slate-500" : isDart ? "text-violet-400" : "text-teal-400"

	const obsDate = obs ? new Date(obs.time) : null
	const showStationId = !!buoy.name && buoy.name !== buoy.station_id

	return (
		<>
			<div className="space-y-1">
				<div className="text-[18px] leading-snug font-semibold text-white line-clamp-2">
					{buoy.name || buoy.station_id}
				</div>
				<div className="flex items-center gap-2 text-xs">
					{showStationId && (
						<span className="font-mono uppercase text-slate-400">{buoy.station_id}</span>
					)}
					<CategoryBadge category={category} />
				</div>
			</div>

			{obsDate && (
				<div className="flex items-center gap-2 text-xs text-slate-400">
					<span>Observed {formatTimeAgo(obsDate)}</span>
					<span className="text-slate-600">&bull;</span>
					<span>{obsDate.toLocaleString()}</span>
				</div>
			)}

			{isDart ? (
				<DartBanner obs={obs} history={history} />
			) : isDefunct ? (
				<DefunctView obs={obs} />
			) : obs?.wave_height != null ? (
				<WaveHero obs={obs} trend={waveTrend} />
			) : obs?.water_level != null ? (
				<TideHero obs={obs} history={history} />
			) : null}

			<MoreSection compact={ctx.compact}>
				{buoy.buoycam && <BuoycamSection stationId={buoy.station_id} accentText={accentText} />}

				<StationMeta buoy={buoy} accentText={accentText} />

				{!isDefunct && !isDart && obs && (
					<ConditionsSection
						obs={obs}
						history={history}
						accentText={accentText}
					/>
				)}

				<CoordFooter
					lat={buoy.lat}
					lon={buoy.lon}
					extra={attributionLine(buoy)}
					className="pt-1"
				/>
			</MoreSection>
		</>
	)
}

/**
 * NDBC BuoyCAM panorama: a 2880x300 strip of six deck-camera views, rendered
 * at its natural aspect and linked to the full-size image. The origin serves
 * an error-card JPEG (same size) when no photo is <16h old — BuoyCAMs only
 * shoot in daylight — so that state displays as the card NDBC provides.
 * Network failures hide the section entirely.
 */
function BuoycamSection({ stationId, accentText }: { stationId: string; accentText: string }) {
	const [tick, setTick] = useState(() => Date.now())
	const [failed, setFailed] = useState(false)

	useEffect(() => {
		const interval = setInterval(() => setTick(Date.now()), BUOYCAM_REFRESH_MS)
		return () => clearInterval(interval)
	}, [])

	if (failed) return null

	const imageUrl = `${BUOYCAM_IMAGE_URL(stationId)}&eb=${tick}`

	return (
		<div className="space-y-1.5">
			<SectionTitle accentClass={accentText}>BuoyCAM</SectionTitle>
			<a
				href={BUOYCAM_IMAGE_URL(stationId)}
				target="_blank"
				rel="noopener noreferrer"
				title="Open full-size BuoyCAM panorama"
				className="pointer-events-auto block rounded-lg overflow-hidden border border-white/10 bg-slate-900/60 hover:border-white/25 transition-colors"
			>
				<img
					src={imageUrl}
					alt={`BuoyCAM panorama from station ${stationId}`}
					className="block w-full h-auto"
					onError={() => setFailed(true)}
				/>
			</a>
		</div>
	)
}

function CategoryBadge({ category }: { category: BuoyCategory }) {
	const colorClass = category === "dart" ? "bg-violet-500/20 text-violet-300 border border-violet-400/30"
		: category === "met" || category === "wave" ? "bg-teal-500/20 text-teal-300 border border-teal-400/30"
		: category === "tide" ? "bg-sky-500/20 text-sky-300 border border-sky-400/30"
		: "bg-slate-500/20 text-slate-400 border border-slate-400/30"
	return <Badge colorClass={colorClass}>{cBuoyCategoryLabel[category]}</Badge>
}

/** Station identity: type, program, and sensor capabilities */
function StationMeta({ buoy, accentText }: { buoy: BuoyEntry; accentText: string }) {
	const hasInfo = buoy.type || buoy.pgm || buoy.met || buoy.currents || buoy.water_quality
	if (!hasInfo) return null

	const capabilities = [
		buoy.met && "Met",
		buoy.currents && "Currents",
		buoy.water_quality && "Water Quality"
	].filter(Boolean)

	return (
		<div className="space-y-1.5">
			<SectionTitle accentClass={accentText}>Station</SectionTitle>
			<div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
				{buoy.type && <InfoField label="Type" value={buoy.type.toUpperCase()} />}
				{buoy.pgm && <InfoField label="Program" value={buoy.pgm} />}
				{capabilities.length > 0 && (
					<div className="col-span-2 min-w-0">
						<span className="text-slate-500">Sensors </span>
						<span className="text-slate-300">{capabilities.join(", ")}</span>
					</div>
				)}
			</div>
		</div>
	)
}

function DefunctView({ obs }: { obs: BuoyObservation | null }) {
	return (
		<div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-3 space-y-2">
			<div className="flex items-center gap-2">
				<svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-amber-500 shrink-0">
					<path d="M7 1L13 12H1L7 1Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
					<line x1="7" y1="5" x2="7" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
					<circle cx="7" cy="10" r="0.75" fill="currentColor" />
				</svg>
				<span className="text-xs text-amber-400">This station has not reported recently</span>
			</div>
			{obs && (
				<div className="text-xs text-slate-500 space-y-1">
					<div>Last known data:</div>
					<div className="grid grid-cols-2 gap-x-4 gap-y-1">
						{obs.air_temp != null && <InfoField label="Air" value={`${Math.round(convertTemperature(obs.air_temp, 0))}${getTemperatureUnit()}`} />}
						{obs.water_temp != null && <InfoField label="Water" value={`${Math.round(convertTemperature(obs.water_temp, 0))}${getTemperatureUnit()}`} />}
						{obs.wave_height != null && <InfoField label="Waves" value={formatWaveHeight(obs.wave_height)} />}
						{obs.wind_speed != null && <InfoField label="Wind" value={formatSpeed(obs.wind_speed)} />}
					</div>
				</div>
			)}
		</div>
	)
}

/**
 * DART tsunameters measure bottom pressure as water-column height. The
 * hero is the latest height with its reporting mode (event mode means the
 * instrument detected a wave) and a 3-day trace, which mostly shows the
 * tide; without a reading the banner says so rather than calling the
 * station inactive
 */
function DartBanner({ obs, history }: { obs: BuoyObservation | null; history: BuoyObservation[] }) {
	const reporting = isDartReporting(obs)
	const eventMode = isDartEventMode(obs)
	const heights = history.filter(o => o.water_column_height != null).map(o => o.water_column_height!)
	const modeLabel = obs?.dart_measurement_type != null ? cDartModeLabel[obs.dart_measurement_type] : null

	return (
		<div className={`rounded-lg border px-3 py-2.5 space-y-2 ${eventMode ? "border-rose-400/30 bg-rose-950/30" : "border-violet-400/10 bg-violet-950/20"}`}>
			<div>
				<div className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${eventMode ? "text-rose-400" : "text-violet-400"}`}>
					{eventMode ? "Tsunami Event Reporting" : "Tsunami Monitoring"}
				</div>
				<div className="mt-1 text-xs text-slate-300">
					Deep-ocean Assessment and Reporting of Tsunamis
				</div>
				{!reporting && (
					<div className="mt-1.5 text-[11px] text-slate-400">
						No recent water-column reading; station position and metadata only.
					</div>
				)}
			</div>
			{reporting && obs?.water_column_height != null && (
				<div className="pt-1 border-t border-white/5 space-y-1.5">
					<div className="flex items-end justify-between gap-3">
						<div>
							<div className="text-2xl leading-none font-light text-white">
								{obs.water_column_height.toFixed(3)} <span className="text-sm text-slate-400">m</span>
							</div>
							<div className="text-[10px] text-slate-400 mt-0.5">Water column height</div>
						</div>
						{modeLabel && (
							<div className={`text-right text-[10px] ${eventMode ? "text-rose-300" : "text-slate-400"}`}>{modeLabel}</div>
						)}
					</div>
					{heights.length >= 4 && (
						<div className="flex items-center gap-2">
							<span className="w-14 shrink-0 text-[10px] uppercase tracking-wider text-slate-500">3 days</span>
							<Sparkline values={heights} className="h-6 flex-1 text-violet-300" />
							<span className="w-16 shrink-0 text-right text-[10px] text-slate-500">
								±{((Math.max(...heights) - Math.min(...heights)) / 2).toFixed(2)} m
							</span>
						</div>
					)}
				</div>
			)}
			{obs?.water_temp != null && (
				<div className="flex items-center justify-between gap-3 pt-1 border-t border-white/5">
					<div>
						<div className="text-2xl leading-none font-light text-white">
							{Math.round(convertTemperature(obs.water_temp, 0))}°
						</div>
						<div className="text-[10px] text-slate-400 mt-0.5">Water Temp</div>
					</div>
					{obs.pressure != null && (
						<div className="text-right">
							<div className="text-lg leading-none font-light text-white">{obs.pressure.toFixed(1)}</div>
							<div className="text-[10px] text-slate-400 mt-0.5">hPa</div>
						</div>
					)}
				</div>
			)}
		</div>
	)
}

/**
 * Tide-gauge hero: water level above MLLW with the tide's direction from
 * the last two readings. The 3-day trace lives in the conditions card
 */
function TideHero({ obs, history }: { obs: BuoyObservation; history: BuoyObservation[] }) {
	if (obs.water_level == null) return null
	const levels = history.filter(o => o.water_level != null)
	const previous = levels.length >= 2 ? levels[levels.length - 2].water_level! : null
	const delta = previous != null ? obs.water_level - previous : null
	const direction = delta == null ? null : delta > 0.02 ? "Rising" : delta < -0.02 ? "Falling" : "Slack"
	const directionClass = direction === "Rising" ? "text-sky-300" : direction === "Falling" ? "text-blue-300" : "text-slate-400"

	return (
		<div className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5">
			<div className="flex items-start justify-between gap-3">
				<div>
					<div className="text-3xl leading-none font-light text-white">
						{formatWaveHeight(obs.water_level)}
					</div>
					<div className="mt-1 text-xs text-slate-300">
						Water level above MLLW
						{direction && <span className={`ml-1.5 ${directionClass}`}>{direction === "Rising" ? "↗" : direction === "Falling" ? "↘" : "→"} {direction}</span>}
					</div>
				</div>
			</div>
		</div>
	)
}

const cSteepnessLabel: Record<string, string> = {
	VERY_STEEP: "Very steep",
	STEEP: "Steep",
	AVERAGE: "Average steepness",
	SWELL: "Swell-dominated",
}

/**
 * Sea-state hero: the headline wave reading with description and trend,
 * then the spectral partition when the station reports one: the swell
 * (long-period energy from a distant system) and the wind sea (short-period
 * chop raised by the local wind)
 */
function WaveHero({ obs, trend }: { obs: BuoyObservation; trend: TrendWord | null }) {
	if (obs.wave_height == null) return null
	const waveDesc = waveDescription(obs.wave_height)
	const hasSwell = obs.swell_height != null && obs.swell_height > 0
	const hasWindSea = obs.wind_wave_height != null && obs.wind_wave_height > 0

	return (
		<div className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 space-y-2">
			<div className="flex items-start justify-between gap-3">
				<div>
					<div className="text-3xl leading-none font-light text-white">
						{formatWaveHeight(obs.wave_height)}
					</div>
					<div className="mt-1 text-xs text-slate-300">
						{waveDesc}
						{trend && <span className="ml-1.5"><TrendBadge trend={trend} /></span>}
					</div>
				</div>
				<div className="text-right text-xs text-slate-400 space-y-1">
					{obs.wave_period != null && <div>Period {obs.wave_period.toFixed(1)}s</div>}
					{obs.avg_wave_period != null && <div>Avg {obs.avg_wave_period.toFixed(1)}s</div>}
					{obs.wave_direction != null && <div>From {formatCardinal(obs.wave_direction)} ({obs.wave_direction}°)</div>}
				</div>
			</div>
			{(hasSwell || hasWindSea) && (
				<div className="pt-2 border-t border-white/5 grid grid-cols-2 gap-x-3 text-xs">
					<PartitionCell
						label="Swell"
						height={hasSwell ? obs.swell_height : null}
						period={obs.swell_period}
						direction={obs.swell_direction}
					/>
					<PartitionCell
						label="Wind sea"
						height={hasWindSea ? obs.wind_wave_height : null}
						period={obs.wind_wave_period}
						direction={obs.wind_wave_direction}
					/>
					{obs.steepness && (
						<div className="col-span-2 mt-1 text-[10px] text-slate-500">{cSteepnessLabel[obs.steepness] ?? obs.steepness}</div>
					)}
				</div>
			)}
		</div>
	)
}

function PartitionCell({ label, height, period, direction }: {
	label: string
	height: number | null
	period: number | null
	direction: number | null
}) {
	return (
		<div className="min-w-0">
			<div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
			{height != null ? (
				<>
					<div className="text-slate-200">
						{formatWaveHeight(height)}
						{period != null && <span className="text-slate-400"> @ {period.toFixed(1)}s</span>}
					</div>
					{direction != null && <div className="text-slate-400">from {formatCardinal(direction)}</div>}
				</>
			) : (
				<div className="text-slate-500">—</div>
			)}
		</div>
	)
}

/**
 * Combined current conditions and 3-day trends: one row per metric with the
 * sparkline (when enough history exists) beside the latest reading, plus a
 * compact grid for point-in-time extras that have no meaningful history row
 */
function ConditionsSection({ obs, history, accentText }: {
	obs: BuoyObservation
	history: BuoyObservation[]
	accentText: string
}) {
	const series = (pick: (o: BuoyObservation) => number | null) =>
		history.filter(o => pick(o) != null).map(o => pick(o)!)

	type Row = { label: string; color: string; values: number[]; current: string | null }
	const rows: Row[] = []

	const addRow = (label: string, color: string, values: number[], current: string | null) => {
		if (current != null || values.length >= 4) {
			rows.push({ label, color, values, current })
		}
	}

	addRow("Waves", "text-teal-300", series(o => o.wave_height),
		obs.wave_height != null ? formatWaveHeight(obs.wave_height) : null)
	addRow("Wind", "text-cyan-300", series(o => o.wind_speed),
		obs.wind_speed != null ? formatSpeed(obs.wind_speed) : null)
	addRow("Pressure", "text-slate-300", series(o => o.pressure),
		obs.pressure != null ? `${obs.pressure.toFixed(0)} hPa` : null)
	addRow("Air", "text-amber-300", series(o => o.air_temp),
		obs.air_temp != null ? `${Math.round(convertTemperature(obs.air_temp, 0))}${getTemperatureUnit()}` : null)
	addRow("Water", "text-sky-300", series(o => o.water_temp),
		obs.water_temp != null ? `${Math.round(convertTemperature(obs.water_temp, 0))}${getTemperatureUnit()}` : null)
	addRow("Tide", "text-blue-300", series(o => o.water_level),
		obs.water_level != null ? formatWaveHeight(obs.water_level) : null)

	const extras: Array<{ label: string; value: string }> = []
	if (obs.wind_direction != null) extras.push({ label: "Wind Dir", value: `${formatCardinal(obs.wind_direction)} (${obs.wind_direction}°)` })
	if (obs.wind_gust != null) extras.push({ label: "Gust", value: formatSpeed(obs.wind_gust) })
	if (obs.pressure_tendency != null) {
		const arrow = obs.pressure_tendency > 0 ? "↑" : obs.pressure_tendency < 0 ? "↓" : "→"
		extras.push({ label: "Tendency", value: `${arrow} ${Math.abs(obs.pressure_tendency).toFixed(1)} hPa` })
	}
	if (obs.dewpoint != null) extras.push({ label: "Dew Point", value: `${Math.round(convertTemperature(obs.dewpoint, 0))}${getTemperatureUnit()}` })
	if (obs.visibility != null) extras.push({ label: "Visibility", value: `${obs.visibility.toFixed(1)} nm` })

	if (rows.length === 0 && extras.length === 0) return null

	return (
		<div className="space-y-1.5">
			<SectionTitle accentClass={accentText}>Conditions &middot; 3 Days</SectionTitle>
			<div className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 space-y-2">
				{rows.map(row => (
					<div key={row.label} className="flex items-center gap-2">
						<span className="w-14 shrink-0 text-[10px] uppercase tracking-wider text-slate-500">{row.label}</span>
						{row.values.length >= 4 ? (
							<Sparkline values={row.values} className={`h-6 flex-1 ${row.color}`} />
						) : (
							<span className="flex-1" />
						)}
						<span className="w-16 shrink-0 text-right text-xs text-slate-200">{row.current ?? "—"}</span>
					</div>
				))}
				{extras.length > 0 && (
					<div className={`grid grid-cols-2 gap-x-4 gap-y-1 text-xs ${rows.length > 0 ? "pt-2 border-t border-white/5" : ""}`}>
						{extras.map(extra => (
							<InfoField key={extra.label} label={extra.label} value={extra.value} />
						))}
					</div>
				)}
			</div>
		</div>
	)
}

/** Minimal inline SVG sparkline; latest value at the right edge */
function Sparkline({ values, className }: { values: number[]; className?: string }) {
	const min = Math.min(...values)
	const max = Math.max(...values)
	const range = max - min
	const points = values.map((value, i) => {
		const x = values.length > 1 ? (i / (values.length - 1)) * 100 : 50
		const y = range > 0 ? 20 - ((value - min) / range) * 16 : 12
		return `${x.toFixed(1)},${y.toFixed(1)}`
	}).join(' ')

	return (
		<svg viewBox="0 0 100 24" preserveAspectRatio="none" className={className}>
			<polyline
				points={points}
				fill="none"
				stroke="currentColor"
				strokeWidth="1.5"
				strokeLinejoin="round"
				strokeLinecap="round"
				vectorEffect="non-scaling-stroke"
			/>
		</svg>
	)
}
